import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useSuspenseMaxTagsQuery } from '#api/tags.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { InlineButton } from '#components/atoms/buttons/inline-button/InlineButton.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ComboboxTags } from '#components/patterns/forms/select/combobox/combobox-tags/ComboboxTags.tsx';
import { InlineTagsList } from '#components/patterns/tags/InlineTagsList.tsx';
import type { Host } from '#dn-types/host.ts';
import { tagNameSchema } from '#schemas/tag/name.ts';
import { checkTagsRestartRequired } from '#screens/hosts/form/hooks/useRestartRequired.ts';
import { useEditHost, useUpdateHostCache } from '#screens/hosts/form/HostForm.queries.ts';
import { RestartHostModal } from '#screens/hosts/form/restart-modal/RestartHostModal.tsx';
import styles from './EditableHostTags.module.css';

const schema = z.object({
  tags: z.array(tagNameSchema),
});

type FormData = {
  tags: string[];
};

type Props = {
  host: Host;
  /** Allow switching the tag list into the tag picker to edit in place */
  canEdit?: boolean;
};

/**
 * A host's tags as a list of linked pills that, when editable, can turn into the tag picker
 * in place — no navigation or modal.
 */
export function EditableHostTags({ host, canEdit }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const editButtonRef = useRef<HTMLButtonElement>(null);

  if (!isEditing) {
    return (
      <div className="flex flex-wrap align-center gap-8">
        <InlineTagsList className="flex-wrap" tagNames={host.tags} linkTags={true} />
        {canEdit ?
          <InlineButton
            ref={editButtonRef}
            onClick={() => {
              setIsEditing(true);
            }}
          >
            {host.tags.length ? 'Edit tags' : '+ Add tags'}
          </InlineButton>
        : null}
      </div>
    );
  }

  return (
    <HostTagsEditor
      host={host}
      onDone={() => {
        // Render the read view synchronously so the edit button exists to take focus —
        // otherwise the unmounting editor drops keyboard users onto <body>
        // eslint-disable-next-line @eslint-react/dom/no-flush-sync -- focus management needs the button in the DOM now
        flushSync(() => {
          setIsEditing(false);
        });
        editButtonRef.current?.focus();
      }}
    />
  );
}

/** The edit half of {@link EditableHostTags}: the tag picker plus Save/Cancel. */
function HostTagsEditor({ host, onDone }: { host: Host; onDone: () => void }) {
  const { control, handleSubmit, formState, setError } = useForm<FormData>({
    defaultValues: { tags: host.tags },
    resolver: zodResolver(schema),
  });
  const editHost = useEditHost();
  const updateHostCache = useUpdateHostCache();
  const allTags = useSuspenseMaxTagsQuery().data.data;
  const [isRestartRequired, setIsRestartRequired] = useState(false);
  const { errors, isSubmitting } = formState;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const updatedHost = await editHost.mutateAsync({
        id: host.id,
        values: {
          name: host.name,
          roleID: host.roleID,
          listenPort: host.listenPort,
          staticAddresses: host.staticAddresses,
          tags: data.tags,
          configOverrides: host.configOverrides,
        },
      });
      await updateHostCache(updatedHost);
      // Adding or removing a tag that carries restart-required overrides changes the host's
      // effective config, so warn the same way the edit form does
      if (checkTagsRestartRequired(data.tags, host.tags, allTags, updatedHost.metadata)) {
        setIsRestartRequired(true);
      } else {
        onDone();
      }
    } catch (error: unknown) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ path, message }) => {
          if (path === 'tags') {
            setError('tags', { message });
            return true;
          }
          setError('root.global', { message });
          return path === undefined;
        });
      } else {
        setError('root.global', { message: 'An unexpected error occurred' });
        trackError(error);
      }
    }
  });

  return (
    <>
      <form onSubmit={onSubmit}>
        <div className={styles.EditableHostTags_editorRow}>
          {/* The row's "Tags" heading already names the field, so hide the picker's own label */}
          <div className={styles.EditableHostTags_editorField}>
            <ComboboxTags
              name="tags"
              control={control}
              hasError={Boolean(errors.tags)}
              labelClassName="visually-hidden"
              focusOnMount={true}
            />
          </div>
          <Button className="shrink-0" variant="link" size="small" onClick={onDone} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button className="shrink-0" type="submit" size="small" disabled={isSubmitting}>
            Save
          </Button>
        </div>
        {errors.root?.global?.message ?
          <ErrorMessage>{errors.root.global.message}</ErrorMessage>
        : null}
        {errors.tags && <ErrorMessage>{errors.tags.message}</ErrorMessage>}
      </form>
      <RestartHostModal isOpen={isRestartRequired} onClose={onDone} platform={host.metadata.platform} />
    </>
  );
}
